// Live feedback updates via EventSource. Mirrors the frozen vocabulary
// from src/events/feedback-bus.ts on the server. Subscribers receive a
// typed event each time staff opens the inbox, a customer posts, or
// another operator triages something — so the UI doesn't lie about the
// state of the queue while you stare at it.

import { useEffect, useRef } from "react";

export type FeedbackEvent =
  | {
      type: "ticket.created";
      ticketId: string;
      licenseId: string | null;
      receivedAt: number;
    }
  | {
      type: "ticket.updated";
      ticketId: string;
      licenseId: string | null;
      status: "new" | "triaged" | "in_progress" | "closed";
      lastMessageAt: number;
    }
  | {
      type: "message.created";
      ticketId: string;
      licenseId: string | null;
      messageId: string;
      sender: "user" | "staff";
      createdAt: number;
    }
  | {
      type: "ticket.read_by_user";
      ticketId: string;
      licenseId: string | null;
      at: number;
    }
  | {
      type: "ticket.read_by_staff";
      ticketId: string;
      licenseId: string | null;
      at: number;
    };

const EVENT_TYPES: FeedbackEvent["type"][] = [
  "ticket.created",
  "ticket.updated",
  "message.created",
  "ticket.read_by_user",
  "ticket.read_by_staff",
];

/**
 * Subscribe to /admin/feedback/events for the lifetime of the calling
 * component. The handler is stored in a ref so consumers can capture
 * setState closures without re-opening the SSE every render.
 *
 * EventSource handles reconnects natively — no extra work needed. The
 * server sends a periodic "ping" comment which we ignore here.
 */
export function useFeedbackEvents(handler: (event: FeedbackEvent) => void, enabled = true): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const es = new EventSource("/admin/feedback/events", { withCredentials: true });
    const dispatch = (raw: MessageEvent) => {
      try {
        const parsed = JSON.parse(raw.data) as FeedbackEvent;
        handlerRef.current(parsed);
      } catch {
        // Ignore malformed payloads — never throw from an SSE handler.
      }
    };
    for (const type of EVENT_TYPES) {
      es.addEventListener(type, dispatch);
    }
    return () => {
      for (const type of EVENT_TYPES) {
        es.removeEventListener(type, dispatch);
      }
      es.close();
    };
  }, [enabled]);
}
